import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, differenceInDays, format, sub } from 'date-fns';
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Title } from "../../themes/Default/components/Title";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { calendarManagement } from "../../hooks/api/CalendarManagement";
import { Events } from "../../themes/Default/components/Events";
import { WeekSelect } from "../../themes/Default/components/Events/components/WeekSelect";
import { Calendar as EventCalendar } from "../../themes/Default/components/Events/components/Calendar";
import { Week } from "../../themes/Default/components/Events/components/Calendar/components/Week";
import { Day } from "../../themes/Default/components/Events/components/Calendar/components/Week/components/Day";
import { Event } from "../../themes/Default/components/Events/components/Calendar/components/Week/components/Day/components/Event";
import { Timeline } from "../../themes/Default/components/Events/components/Calendar/components/Timeline";
import { Time } from "../../themes/Default/components/Events/components/Calendar/components/Timeline/components/Time";
import { generateTimeSlots } from "../../services/core/Time";
import { roles } from "../../services/utils/Roles";
import "./style.css";
import { useUserToken } from "../../hooks/core/UserToken";

const Calendar = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(new Date());
    const [startDayOfWeek, setStartDayOfWeek] = useState(0);
    const [endDayOfWeek, setEndDayOfWeek] = useState(0);
    const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const { useCalendarListMutation } = calendarManagement;
    const [calendarList] = useCalendarListMutation();
    const { getDecoded } = useUserToken()[0];
    const decodedToken = getDecoded();
    
    const gotoAddEvent = (employeeId) => {
        navigate("/calendar/add-event");
    };

    const onClickNextWeek = () => {
        setCurrentWeek(addWeeks(currentWeek, 1));
    }

    const onClickPreviewWeek = () => {
        setCurrentWeek(subWeeks(currentWeek, 1));
    }

    const onChangeDate = (selectedDate) => {
        setSelectedDate(format(selectedDate, "yyyy-MM-dd"));
    }

    const weekDayStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekdayEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

    let days = [];
    let diff = differenceInDays(weekdayEnd, weekDayStart);
    
    for (let i = 0; i <= diff; i++) {
        days.push(addDays(weekDayStart, i));
    }

    const timeSlots = generateTimeSlots(8, 20);

    useEffect(() => {
        const dateFrom = format(weekDayStart, "yyyy-MM-dd");
        const dateTo = format(weekdayEnd, "yyyy-MM-dd");

        calendarList({dateFrom, dateTo}).then((result) => {
            const formattedDates = days.map(day => {
                const formattedDate = format(day, "yyyy-MM-dd");
                return {
                    date: formattedDate,
                    events: result?.data?.records[formattedDate] ?? null
                };
            });
            setEvents(formattedDates);
            setSelectedDate(format(dateFrom, "yyyy-MM-dd"));
        });

    },[currentWeek]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                {!(decodedToken?.roles.includes(roles.STUDENT)) && (
                    <>
                    <Button title="Add event" icon="fa-regular fa-plus" onClick={gotoAddEvent}></Button>
                    </>
                )}
            </TopNavigation>
            <Main>
                <Title>Calendar</Title>
                <Events>
                    <WeekSelect fromDay={weekDayStart} toDay={weekdayEnd} onClickNextWeek={onClickNextWeek} onClickPreviewWeek={onClickPreviewWeek}/>
                    <EventCalendar>
                        <Week>
                            {events?.length > 0 &&
                                events?.map((event, index) => {
                                    return (
                                        <Day 
                                            key={index} 
                                            day={event} 
                                            selectedDate={selectedDate} 
                                            onClick={() => onChangeDate(event.date)}
                                        >
                                            {event.events?.map((subEvent, subIndex) => (
                                                <Event event={subEvent} key={subIndex} eventColor={subEvent.color}/>
                                            ))}
                                        </Day>
                                    );
                                })
                            }
                        </Week>
                        <Timeline day={selectedDate} >
                            {events?.length > 0 &&
                                timeSlots.map((time, index) => {
                                    const event = events.find(item => item.date === selectedDate);
                                    return (
                                        <Time time={time} event={event} key={index} />
                                    )
                                })
                            }
                        </Timeline>
                    </EventCalendar>
                </Events>
            </Main>
        </DefaultLayout>
    );
};

export default Calendar;
